import { FunctionComponent, useCallback, useEffect } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import config from '@config'
import {
  updateAppTheme,
  updateBrowserTheme,
  updateUserTheme,
  UserThemeNonServer,
} from '@utils/theme'
import useBrowserTheme from '@hooks/useBrowserTheme'
import useUserTheme from '@hooks/useUserTheme'

const {
  theme: { windowKey: themeWindowKey },
} = config

const ThemeManager: FunctionComponent = () => {
  const queryClient = useQueryClient()
  const browserTheme = useBrowserTheme()
  const userTheme = useUserTheme()

  const onLocalStorageChanged = useCallback<(event: StorageEvent) => void>(
    (event) => {
      if (event.key === themeWindowKey) {
        try {
          const retrievedUserTheme = localStorage.getItem(
            themeWindowKey,
          ) as UserThemeNonServer | null

          if (retrievedUserTheme) {
            updateUserTheme(queryClient, retrievedUserTheme)
          }
        } catch {}
      }
    },
    [queryClient],
  )

  const onColorSchemeChange = useCallback<(e: MediaQueryListEvent) => void>(
    (e) => {
      updateBrowserTheme(queryClient, e.matches ? 'dark' : 'light')
    },
    [queryClient],
  )

  useEffect(() => {
    let themeMediaQuery = queryClient.getQueryData<MediaQueryList>([
      'theme',
      'media-query',
    ])

    if (!themeMediaQuery) {
      queryClient.setQueryData<MediaQueryList>(
        ['theme', 'media-query'],
        window.matchMedia('(prefers-color-scheme: dark)'),
      )

      themeMediaQuery = queryClient.getQueryData<MediaQueryList>([
        'theme',
        'media-query',
      ]) as MediaQueryList

      themeMediaQuery.addEventListener('change', onColorSchemeChange)

      let userTheme = 'light' as UserThemeNonServer

      try {
        userTheme =
          (localStorage.getItem(themeWindowKey) as UserThemeNonServer) ??
          'light'
      } catch {}

      updateUserTheme(queryClient, userTheme)

      updateBrowserTheme(
        queryClient,
        themeMediaQuery.matches ? 'dark' : 'light',
      )

      updateAppTheme(queryClient)
    }

    return () => {
      const themeMediaQuery = queryClient.getQueryData<MediaQueryList>([
        'theme',
        'media-query',
      ])

      if (themeMediaQuery) {
        themeMediaQuery.removeEventListener('change', onColorSchemeChange)
      }
    }
  }, [onColorSchemeChange, queryClient])

  useEffect(() => {
    addEventListener('storage', onLocalStorageChanged)

    return () => {
      removeEventListener('storage', onLocalStorageChanged)
    }
  }, [onLocalStorageChanged])

  useEffect(() => {
    updateAppTheme(queryClient)
  }, [queryClient, browserTheme, userTheme])

  return null
}

export default ThemeManager
